import type { RiskClass } from "./auth.js";

export interface Request {
  pageNum: number;
  pageSize: number;
  [property: string]: any;
}

/**
 * 排序类型 DESC/ASC
 */
export enum OrderAspect {
  Asc = "ASC",
  Desc = "DESC",
}

/**
 * 团队分页列表请求参数
 */
export interface TeamListRequest {
  /**
   * 排序类型 DESC/ASC
   */
  orderAspect?: OrderAspect;
  /**
   * 排序字段
   */
  orderColumn?: string;
  /**
   * 当前页数
   */
  pageNum?: number;
  /**
   * 每页数据条数
   */
  pageSize?: number;
  [property: string]: any;
}

/**
 * beefintech.ifa.user.domain.vo.UserPublicInfo
 *
 * UserPublicInfo
 */
export interface UserPublicInfo {
  email?: string;
  empNo?: string;
  iaCode?: string;
  isIFA?: boolean;
  jobName?: string;
  name?: string;
  rankName?: string;
  teamId?: number;
  teamName?: string;
  uid?: number;
  [property: string]: any;
}

/**
 * UserBaseInfo
 */
export interface UserBaseInfo {
  /**
   * 工号
   */
  empNo?: string;
  /**
   * 名称
   */
  name?: string;
  /**
   * 昵称/别名
   */
  nickname?: string;
  /**
   * 团队名称
   */
  teamName?: string;
  uid?: number;
  [property: string]: any;
}

/**
 * beefintech.ifa.user.domain.vo.UserListVO
 *
 * UserListVO
 */
export interface UserListVO {
  account?: string;
  areaCode?: number;
  directTutorInfo?: UserBaseInfo;
  email?: string;
  employmentStatus?: EmploymentStatus;
  empNo?: string;
  entryDate?: string;
  iaCode?: string;
  isIfa?: boolean;
  isInternal?: boolean;
  jobName?: string;
  name?: string;
  nickname?: string;
  phone?: string;
  rankName?: string;
  referrerInfo?: UserBaseInfo;
  remark?: string;
  status?: Status;
  teamId?: number;
  teamName?: string;
  uid?: number;
  [property: string]: any;
}

/**
 * beefintech.ifa.user.domain.vo.UserWithRoleListVO
 *
 * UserWithRoleListVO
 */
export interface UserWithRoleListVO {
  account?: string;
  employmentStatus?: EmploymentStatus;
  empNo?: string;
  entryDate?: string;
  isInternal?: boolean;
  name?: string;
  personnelType?: PersonnelType;
  roleList?: RoleList[];
  status?: Status;
  uid?: number;
  [property: string]: any;
}
export interface RoleList {
  roleId?: string;
  name?: string;
}

/**
 * Pageable
 */
export interface Pageable {
  /**
   * Returns the offset to be taken according to the underlying page and page size.
   */
  offset?: number;
  /**
   * Returns whether the current{@link Pageable} contains pagination information.
   */
  paged?: boolean;
  /**
   * Returns the page to be returned.
   */
  pageNumber?: number;
  /**
   * Returns the number of items to be returned.
   */
  pageSize?: number;
  /**
   * Returns the sorting parameters.
   */
  sort?: Sort[];
  /**
   * Returns whether the current{@link Pageable} does not contain pagination information.
   */
  unpaged?: boolean;
  [property: string]: any;
}

/**
 * org.springframework.data.domain.Sort
 *
 * Sort
 */
export interface Sort {
  /**
   * Returns whether sorting for this property shall be ascending.
   */
  ascending?: boolean;
  /**
   * Returns whether sorting for this property shall be descending.
   */
  descending?: boolean;
  direction?: Direction;
  ignoreCase?: boolean;
  nullHandling?: NullHandling;
  property?: string;
  [property: string]: any;
}

export enum Direction {
  Asc = "ASC",
  Desc = "DESC",
}

export enum NullHandling {
  Native = "NATIVE",
  NullsFirst = "NULLS_FIRST",
  NullsLast = "NULLS_LAST",
}

/**
 * 用户语言
 */
export enum Lang {
  EnUs = "EN_US",
  ZhCN = "ZH_CN",
  ZhHk = "ZH_HK",
}

/**
 * 在职状态
 */
export enum EmploymentStatus {
  OnJob = "ON_JOB",
  Resigned = "RESIGNED",
  Vacation = "VACATION",
}

/**
 * 牌照類型
 */
export enum LicenseType {
  License1234 = "LICENSE_1234",
  License12345 = "LICENSE_12345",
  License1235_GeneralLongLink = "LICENSE_1235_GENERAL_LONG_LINK",
  License123_GeneralLongNoLink = "LICENSE_123_GENERAL_LONG_NO_LINK",
  License134 = "LICENSE_134",
  License1345 = "LICENSE_1345",
  License135_LongLink = "LICENSE_135_LONG_LINK",
  License3_LongNoLink = "LICENSE_3_LONG_NO_LINK",
}

/**
 * 掛牌狀態
 */
export enum ListingStatus {
  Listed = "LISTED",
  OffListed = "OFF_LISTED",
  UnListed = "UN_LISTED",
}

/**
 * 掛牌有效期
 */
export enum ListingValidityPeriod {
  OneYear = "ONE_YEAR",
  ThreeYears = "THREE_YEARS",
  TwoYears = "TWO_YEARS",
}

/**
 * 人员类型
 */
export enum PersonnelType {
  Ifa = "IFA",
  InternalStaff = "INTERNAL_STAFF",
  Referrer = "REFERRER",
}

/**
 * 账号状态
 */
export enum Status {
  Deleted = "DELETED",
  Disabled = "DISABLED",
  Enabled = "ENABLED",
}

/**
 * 联系信息
 * ContactInfo
 */
export interface ContactInfo {
  /**
   * 内地手机号区号
   */
  areaCode?: string;
  /**
   * 其他手机号区号
   */
  otherAreaCode?: string;
  /**
   * 其他邮箱
   */
  otherEmail?: string;
  /**
   * 其他手机号
   */
  otherPhone?: string;
  /**
   * 内地手机号
   */
  phone?: string;
  /**
   * 工作邮箱
   */
  workEmail?: string;
  [property: string]: any;
}

/**
 * 掛牌信息
 * ListingInfo
 */
export interface ListingInfo {
  /**
   * 是否禁止簽單（true=禁止，false=允许）
   */
  isForbidSign?: boolean;
  /**
   * 牌照類型
   */
  licenseType?: LicenseType;
  /**
   * 掛牌終止時間
   */
  listingEndTime?: string;
  /**
   * 掛牌開始時間
   */
  listingStartTime?: string;
  /**
   * 掛牌狀態
   */
  listingStatus?: ListingStatus;
  /**
   * 掛牌有效期
   */
  listingValidityPeriod?: ListingValidityPeriod;
  [property: string]: any;
}

/**
 * MPFA 掛牌信息
 * MpfaListingInfo
 */
export interface MpfaListingInfo {
  /**
   * MPFA Code
   */
  mpfaCode?: string;
  /**
   * 是否禁止簽單（true=禁止，false=允许）
   */
  isForbidSign?: boolean;
  /**
   * 掛牌終止時間
   */
  listingEndTime?: string;
  /**
   * 掛牌開始時間
   */
  listingStartTime?: string;
  /**
   * 掛牌狀態
   */
  listingStatus?: ListingStatus;
  /**
   * 掛牌有效期
   */
  listingValidityPeriod?: ListingValidityPeriod;
  [property: string]: any;
}

/**
 * UserDTO
 */
export interface UserSaveRequest {
  /**
   * 登录账号，唯一
   */
  account: string;
  /**
   * 中文姓名
   */
  chineseName?: string;
  /**
   * 联系信息
   */
  contactInfo?: ContactInfo;
  /**
   * 当前职级ID
   */
  currentRankId?: number;
  /**
   * 直接辅导人ID
   */
  directTutorUid?: number;
  /**
   * 在职状态
   */
  employmentStatus?: EmploymentStatus;
  /**
   * 员工工号
   */
  empNo?: string;
  /**
   * 英文姓名
   */
  englishName?: string;
  /**
   * 入职日期
   */
  entryDate?: string;
  /**
   * 入职时初始职级ID
   */
  entryJobId?: number;
  /**
   * iaCode
   */
  iaCode?: string;
  /**
   * MPFA 掛牌信息
   */
  mpfaListingInfo?: MpfaListingInfo;
  id?: number;
  /**
   * 是否IFA（理财顾问）
   */
  isIfa?: boolean;
  /**
   * 是否内部用户
   */
  isInternal?: boolean;
  /**
   * 当前职务ID
   */
  jobId?: number;
  /**
   * 用户语言
   */
  lang?: Lang;
  /**
   * 掛牌信息
   */
  listingInfo?: ListingInfo;
  /**
   * 昵称/别名
   */
  nickname?: string;
  /**
   * 登录密码（建议加密存储）
   */
  password?: string;
  /**
   * 人员类型
   */
  personnelType?: PersonnelType;
  /**
   * 备注信息
   */
  remark?: string;
  /**
   * 离职日期
   */
  resignDate?: string;
  /**
   * 标识用户所属角色
   */
  roleIds?: number[];
  /**
   * 账号状态
   */
  status?: Status;
  /**
   * 团队ID
   */
  teamId?: number;
  /**
   * 推荐人ID
   */
  referrerUid?: number;
  [property: string]: any;
}

/**
 * Page - 分页信息
 */
export interface Page {
  /**
   * 每页数据条数
   */
  size?: number;
  /**
   * 当前页码（从0开始）
   */
  number?: number;
  /**
   * 总数据条数
   */
  totalElements?: number;
  /**
   * 总页数
   */
  totalPages?: number;
  [property: string]: any;
}

/**
 * PageUserListVO - 用户列表响应
 */
export interface Response {
  page?: Page;
  content?: UserListVO[];
  [property: string]: any;
}

/**
 * 当前团队用户分页列表响应
 */
export interface CurrentTeamUserListResponse {
  content?: UserListVO[];
  empty?: boolean;
  first?: boolean;
  last?: boolean;
  number?: number;
  numberOfElements?: number;
  pageable?: Pageable;
  size?: number;
  sort?: Sort[];
  total?: number;
  totalElements?: number;
  totalPages?: number;
  [property: string]: any;
}

/**
 * PageUserWithRoleListVO - 权限设置人员列表响应
 */
export interface UserWithRoleListResponse {
  content?: UserWithRoleListVO[];
  page?: Page;
  [property: string]: any;
}

/**
 * TeamListVO - 团队列表
 */
export interface TeamListVO {
  id?: number;
  /**
   * 团队负责人ID
   */
  managerId?: number;
  /**
   * 团队负责人名称
   */
  managerName?: string;
  /**
   * 团队人数
   */
  number?: number;
  /**
   * 团队名称
   */
  teamName?: string;
  [property: string]: any;
}

/**
 * PageTeamListVO - 团队分页列表响应
 */
export interface TeamListResponse {
  content?: TeamListVO[];
  /**
   * Returns whether the current{@link Streamable} is empty.
   */
  empty?: boolean;
  first?: boolean;
  last?: boolean;
  number?: number;
  numberOfElements?: number;
  pageable?: Pageable;
  size?: number;
  sort?: Sort[];
  total?: number;
  totalElements?: number;
  totalPages?: number;
  [property: string]: any;
}

/**
 * UserTeamDTO - 团队创建/更新请求
 */
export interface TeamCreateRequest {
  id?: number;
  /**
   * 团队负责人ID
   */
  teamManagerId?: number;
  /**
   * 团队名称
   */
  teamName?: string;
  [property: string]: any;
}

/**
 * UserInfoDetail - 用户详细信息
 */
export interface UserInfoDetail {
  /**
   * 员工工号
   */
  empNo?: string;
  /**
   * 登录账号
   */
  account?: string;
  /**
   * 中文姓名
   */
  chineseName?: string;
  /**
   * 英文姓名
   */
  englishName?: string;
  /**
   * 昵称/别名
   */
  nickname?: string;
  /**
   * 联系信息
   */
  contactInfo?: ContactInfo;
  /**
   * 手机区号
   */
  areaCode?: number;
  /**
   * 手机号
   */
  phone?: string;
  /**
   * 工作邮箱
   */
  workEmail?: string;
  /**
   * 当前职务ID
   */
  jobId?: number;
  /**
   * 职务名称
   */
  jobName?: string;
  /**
   * 入职时初始职级ID
   */
  entryJobId?: number;
  /**
   * 入职日期
   */
  entryDate?: string;
  /**
   * 当前职级ID
   */
  currentRankId?: number;
  /**
   * 职级名称
   */
  rankName?: string;
  /**
   * 账号状态
   */
  status?: Status;
  /**
   * 在职状态
   */
  employmentStatus?: EmploymentStatus;
  /**
   * 是否内部用户
   */
  isInternal?: boolean;
  /**
   * 是否IFA（理财顾问）
   */
  isIfa?: boolean;
  /**
   * iaCode
   */
  iaCode?: string;
  /**
   * 掛牌信息
   */
  listingInfo?: ListingInfo;
  /**
   * MPFA 掛牌信息
   */
  mpfaListingInfo?: MpfaListingInfo;
  /**
   * 用户语言
   */
  lang?: Lang;
  /**
   * 备注信息
   */
  remark?: string;
  /**
   * 推荐人 UID
   */
  referrerUid?: number;
  /**
   * 推荐人姓名
   */
  referrerName?: string;
  /**
   * 直接督导人 UID
   */
  directTutorUid?: number;
  /**
   * 直接督导人姓名
   */
  directTutorName?: string;
  [property: string]: any;
}

/**
 * I18n - 多语言
 */
export interface I18N {
  /**
   * 英文
   */
  enUS?: string;
  /**
   * 日文
   */
  jaJP?: string;
  /**
   * 韩文
   */
  koKR?: string;
  /**
   * 简体
   */
  zhHansCN?: string;
  /**
   * 繁体
   */
  zhHantHK?: string;
  [property: string]: any;
}

/**
 * Country - 国家
 */
export interface Country {
  /**
   * 区号
   */
  areaCode?: string;
  /**
   * 中文名称
   */
  cname?: string;
  /**
   * 国家所在的七大洲对应的id
   */
  continentId?: number;
  /**
   * 国家码
   */
  countryCode?: string;
  fbCountryCode?: string;
  /**
   * 分组： A-Z
   */
  group?: string;
  /**
   * 是否热门国家
   */
  hot?: boolean;
  /**
   * 热门国家排序
   */
  hotRank?: number;
  /**
   * 编号
   */
  id?: number;
  /**
   * 国家名称
   */
  name?: string;
  /**
   * 名称多语言
   */
  nameI18n?: I18N;
  /**
   * 中文拼音
   */
  pinyin?: string;
  /**
   * 风险等级
   */
  riskClass?: RiskClass;
  [property: string]: any;
}

export type NewCountryGroup = {
  /**
   * 分组国家列表
   */
  countries?: Country[];
  /**
   * 数量
   */
  count?: { [key: string]: any };
  [property: string]: any;
};

/**
 * TR列表请求参数
 */
export interface TrListRequest {
  pageNum?: number;
  pageSize?: number;
  keyword?: string;
  teamId?: number;
  uids?: number[];
  isIfa?: boolean;
  orderColumn?: string;
  orderAspect?: "ASC" | "DESC";
}

/**
 * TR列表项
 */
export interface AppUserListVO {
  uid?: number;
  name?: string;
  iaCode?: string;
}

/**
 * TR列表响应
 */
export interface PageAppUserListVO {
  content?: AppUserListVO[];
  empty?: boolean;
  first?: boolean;
  last?: boolean;
  number?: number;
  numberOfElements?: number;
  pageable?: Pageable;
  size?: number;
  sort?: Sort[];
  total?: number;
  totalElements?: number;
  totalPages?: number;
}

export enum CountryRiskClass {
  HOT = "HOT",
  LOW_RISK = "LOW_RISK",
  MEDIUM_RISK = "MEDIUM_RISK",
  HIGH_RISK = "HIGH_RISK",
  OTHER = "OTHER",
}

export enum CountryTypeEnum {
  All = "ALL",
  Hot = "HOT",
  LowRisk = "LOW_RISK",
  MediumRisk = "MEDIUM_RISK",
  HighRisk = "HIGH_RISK",
  Other = "OTHER",
}
